import {copyText} from '@github-ui/copy-to-clipboard'
import type {ClientDiffLine, DiffLine, DiffOrientation} from '../types'
import {getContentFromLines, groupDiffLines, isEmptyDiffLine} from './line-helpers'
import type {LineRange} from '@github-ui/diffs/types'
import DiffRowSelectedChecker from './diff-row-selected-checker'

export async function copyCode({
  diffLine,
  diffLines,
  getSelectedDiffLines,
  getSelectedLineRange,
  isSplit,
  isLeftSide,
}: {
  diffLine: ClientDiffLine
  diffLines: DiffLine[]
  getSelectedDiffLines: () => DiffLine[]
  getSelectedLineRange: () => LineRange | undefined
  isSplit: boolean
  isLeftSide: boolean | undefined
}) {
  const highlightedTextSelection = window.getSelection()

  if (highlightedTextSelection && highlightedTextSelection.toString() !== '') {
    document.execCommand('copy')
    return
  }

  const isSingleCellSelection = getSelectedDiffLines().length <= 1

  let text

  switch (true) {
    case isSingleCellSelection:
      text = getDiffSingleLineCode(diffLine)
      break
    case isSplit:
      text = getSplitDiffMultiLineCode({
        diffLines,
        diffOrientation: isLeftSide ? 'left' : 'right',
        getSelectedLineRange,
      })
      break
    default:
      text = getUnifiedDiffMultiLineCode({diffLines, getSelectedLineRange})
  }

  await copyText(text)
}

const getUnifiedDiffMultiLineCode = ({
  diffLines,
  getSelectedLineRange,
}: {
  diffLines: DiffLine[]
  getSelectedLineRange: () => LineRange | undefined
}) => {
  const lineRange = getSelectedLineRange()
  if (!lineRange) return ''

  const {startOrientation, endOrientation, startLineNumber, endLineNumber} = lineRange

  let firstLineIndex = 0
  let secondLineIndex = 0

  // We only need to search for new first and end indexes if there is more than one diffline.
  if (diffLines.length > 1) {
    firstLineIndex = diffLines.findIndex(line =>
      startOrientation === 'left' ? line.left === startLineNumber : line.right === startLineNumber,
    )
    secondLineIndex = diffLines.findIndex(line =>
      endOrientation === 'left' ? line.left === endLineNumber : line.right === endLineNumber,
    )
  }

  return getContentFromLines(diffLines.slice(firstLineIndex, secondLineIndex + 1))
}

const getDiffSingleLineCode = (diffLine: ClientDiffLine) => {
  if (isEmptyDiffLine(diffLine) || !diffLine) return ''

  return getContentFromLines([diffLine])
}

const getSplitDiffMultiLineCode = ({
  diffLines,
  diffOrientation,
  getSelectedLineRange,
}: {
  diffOrientation: DiffOrientation | undefined
  diffLines: DiffLine[]
  getSelectedLineRange: () => LineRange | undefined
}) => {
  if (diffOrientation === undefined) return ''

  const selectedLineRange = getSelectedLineRange()

  if (!selectedLineRange) return ''

  const grouped = groupDiffLines(diffLines)
  const leftLines = grouped.leftLines
  const rightLines = grouped.rightLines

  const {selectedRightLines, selectedLeftLines} = DiffRowSelectedChecker({
    selectedLineRange,
    leftLines,
    rightLines,
  })

  return diffOrientation === 'left' ? getContentFromLines(selectedLeftLines) : getContentFromLines(selectedRightLines)
}
